import { useLayoutEffect, useRef } from 'react';
import { NavigationType, useLocation, useNavigationType } from 'react-router';
import { scrollStore } from '@/motion/scroll';

/**
 * Every link that opens another page shows that page from its top (D-372). The router's own scroll restoration left the
 * old scroll position in place when the new page was shorter than the old one (the browser just clamped it), so a click
 * on "Our Collections" from the foot of the home page landed in the middle of the shop. Rules:
 *   • a new page (the path changes) reached by a link, a redirect or `navigate()` → scroll to 0, instantly;
 *   • the browser's Back / Forward → untouched (`<ScrollRestoration />` puts the visitor back where they were);
 *   • an address with a `#hash` → untouched (the anchor decides);
 *   • the same page with another query string (a filter, a sort order) → untouched.
 * It runs in the layout effect of the commit that shows the new page, so there is no frame at the old position (and
 * inside a View Transition it is part of the new snapshot).
 */
export function ScrollToTop(): null {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const previous = useRef(pathname);

  useLayoutEffect(() => {
    const changed = previous.current !== pathname;
    previous.current = pathname;
    if (!changed || navigationType === NavigationType.Pop || hash !== '') return;
    scrollStore.scrollTo(0, { immediate: true });
  }, [pathname, hash, navigationType]);

  return null;
}
